import React from "react";
import type { Project } from "../../../../api/useProjects";
import { useProjects } from "../../../../api/useProjects";
import t from "../../../../i18n";
import useModal from "@ui/lib/hooks/useModal";
import Button from "@ui/Button";
import Modal from "@ui/Modal";
import { useCanUserDoOnProject } from "../../../canUserDo";
import { canUserDoOnProject } from "diagnostics-domain/canUserDo";
import CopyingProjectAnalysesModalForm from "./CopyingProjectAnalysesModalForm";
import { useUser } from "../../../../api/useUser";

interface CopyingProjectAnalysesProps {
  project: Project;
}

const useProjectsWithIdenticalSource = (sourceProject: Project, targetProjects: Project[]) => {
  const user = useUser();
  return React.useMemo(
    () =>
      targetProjects.filter(
        p =>
          p.sourceId === sourceProject.sourceId &&
          p.id !== sourceProject.id &&
          canUserDoOnProject(user, "PROJECT_READ", p.id),
      ),
    [sourceProject, targetProjects, user],
  );
};

export const CopyingProjectAnalysesAction = ({ project }: CopyingProjectAnalysesProps) => {
  const projects = useProjects();
  const { openModal, closeModal, modal: isModalOpen } = useModal();

  const canEdit = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const projectsWithIdenticalSource = useProjectsWithIdenticalSource(project, projects);

  if (
    !canEdit ||
    projectsWithIdenticalSource.length === 0 ||
    project.analysesAggregate?.activeAnalysisCount ||
    project.initInProgress !== false
  )
    return null;

  return (
    <>
      <Button variant="secondary" slim type="button" onClick={openModal}>
        {t("project.button.copying_project_analyses")}
      </Button>
      <Modal
        title={t("project.button.copying_project_analyses")}
        subtitle={t("project.modal.copying_project_analyses.subtitle")}
        isOpen={isModalOpen}
        onRequestClose={closeModal}
      >
        <CopyingProjectAnalysesModalForm
          project={project}
          projectsWithIdenticalSource={projectsWithIdenticalSource}
          onRequestClose={closeModal}
        />
      </Modal>
    </>
  );
};
